import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FileUploadModule } from 'ng2-file-upload';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';

import { SharedDirectivesModule } from '../directives/shared-directives.module';
import { SharedPipesModule } from '../pipes/shared-pipes.module';

import { BtnLoadingComponent } from './btn-loading/btn-loading.component';
import { ListLegendComponent } from './list-legend/list-legend.component';
import { LoaderComponent } from './loader/loader.component';
import { Ng2FileDropComponent } from './ng2-file-drop/ng2-file-drop.component';
import { StarRatingComponent } from './star-rating/star-rating.component';
import { StatsListItemComponent } from './stats-list-item/stats-list-item.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { DragDropLinksComponent } from './drag-and-drop/drag-drop-links/drag-drop-links.component';
import { DragDropEnabledLinksComponent } from './drag-and-drop/drag-drop-enabled-links/drag-drop-enabled-links.component';
import { CustomRangePanelComponent } from './custom-date-range-picker/custom-range-panel/custom-range-panel.component';
import { CustomHeaderDateRangePickerComponent } from './custom-date-range-picker/custom-header-date-range-picker';
import {
	DoughnutPieChartComponent,
	DoughnutPieChartLeastClickedComponent,
	LineChartComponent,
	BarChartComponent,
	RadialStackedChartComponent
} from './charts';

import { ChartLegendComponent } from './chart-legend/chart-legend.component';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { translateServerLoaderFactory } from '@app-utils/translate-server-loader';
import { CreditCardIconComponent } from './credit-card-brand/credit-card-icon.component';
import { QRCodeComponent } from './qr-code/qr-code.component';
import { QRCodeModule } from 'angularx-qrcode';
import { BtnLoadMoreComponent } from './btn-load-more/btn-load-more.component';
import { PublicFooterComponent } from './public-footer';
import { PublicHeaderComponent } from './public-header';
import { PublicCardPropertyComponent } from './public-card-property';
import { PublicMlsComponent } from './public-mls/public-mls.component';
import { SearchFilterComponent } from './search-filter/search-filter.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OwnerListCardComponent } from './owner-list-card/owner-list-card.component';
import { SearchInputComponent } from './search-input/search-input.component';
import { MlsSelectComponent } from './mls-select/mls-select.component';
import { PropertyCardComponent } from './property-card/property-card.component';
import { BedBathSelectComponent } from './bed-bath-select/bed-bath-select.component';
import { PromotedContentOfferPreviewComponent } from './promoted-content-offer-preview/promoted-content-offer-preview.component';
import { OwnerPreviewComponent } from './owner-preview/owner-preview.component';
import { RealEstateListingPreviewComponent } from './real-estate-listing-preview/real-estate-listing-preview.component';
import { LocationPickerComponent } from './location-picker/location-picker.component';
import { RadioButtonComponent } from './radio-button';
import { FilterMultiSelectComponent } from './filter-multi-select/filter-multi-select.component';
import { OptionButtonGroupComponent } from './option-button-group/option-button-group.component';
import { FilterChipComponent } from './filter-chip/filter-chip.component';
import { GroupButtonComponent } from './group-button/group-button.component';
import { ToggleSwitchComponent } from './toggle-switch/toggle-switch.component';
import { RealEstateOfficeMultiSelectComponent } from './real-estate-office-multi-select/real-estate-office-multi-select.component';
import { RealEstateFeaturesMultiSelectComponent } from './real-estate-features-multi-select/real-estate-features-multi-select.component';
import { NonNegativeNumberInputComponent } from './non-negative-number-input/non-negative-number-input.component';
import { VimeoPlayerComponent } from './vimeo-player/vimeo-player.component';
import { LayoutVariationSelectorComponent } from './layout-variation-selector/layout-variation-selector.component';
import { ListingDetailsPromotedContentComponent } from './listing-details-promoted-content';

const components = [
	BtnLoadingComponent,
	Ng2FileDropComponent,
	LoaderComponent,
	StarRatingComponent,
	ListLegendComponent,
	StatsListItemComponent,
	DragDropLinksComponent,
	DragDropEnabledLinksComponent,
	CustomRangePanelComponent,
	CustomHeaderDateRangePickerComponent,
	DoughnutPieChartComponent,
	DoughnutPieChartLeastClickedComponent,
	LineChartComponent,
	BarChartComponent,
	RadialStackedChartComponent,
	ChartLegendComponent,
	CreditCardIconComponent,
	LocationPickerComponent,
	QRCodeComponent,
	BtnLoadMoreComponent,
	PublicFooterComponent,
	PublicHeaderComponent,
	PublicCardPropertyComponent,
	PublicMlsComponent,
	SearchFilterComponent,
	OwnerListCardComponent,
	SearchInputComponent,
	MlsSelectComponent,
	BedBathSelectComponent,
	PropertyCardComponent,
	PromotedContentOfferPreviewComponent,
	RealEstateListingPreviewComponent,
	OwnerPreviewComponent,
	RadioButtonComponent,
	FilterMultiSelectComponent,
	OptionButtonGroupComponent,
	FilterChipComponent,
	GroupButtonComponent,
	ToggleSwitchComponent,
	RealEstateOfficeMultiSelectComponent,
	RealEstateFeaturesMultiSelectComponent,
	NonNegativeNumberInputComponent,
	LayoutVariationSelectorComponent,
	VimeoPlayerComponent,
	ListingDetailsPromotedContentComponent
];

@NgModule({
	imports: [
		CommonModule,
		RouterModule,
		SharedPipesModule,
		SharedDirectivesModule,
		PerfectScrollbarModule,
		NgbModule,
		FileUploadModule,
		TranslateModule.forChild({
			loader: {
				provide: TranslateLoader,
				useFactory: translateServerLoaderFactory,
				deps: [HttpClient]
			}
		}),
		DragDropModule,
		MatInputModule,
		MatFormFieldModule,
		MatCheckboxModule,
		MatSlideToggleModule,
		MatRadioModule,
		MatCardModule,
		MatButtonModule,
		MatIconModule,
		MatSidenavModule,
		MatSliderModule,
		MatAutocompleteModule,
		MatSelectModule,
		MatChipsModule,
		QRCodeModule,
		FormsModule,
		ReactiveFormsModule
	],
	declarations: components,
	exports: components
})
export class SharedComponentsModule {}
